import {
  Monitor,
  Scan,
  HeartPulse,
  Stethoscope,
  Brain,
  Bone,
  Activity,
  Droplets,
} from 'lucide-react';

/**
 * Contact information for the diagnostic center
 * Source: https://mtufaildiagnosticcenter.com
 */
export const CONTACT_INFO = {
  phone: '+923000478311',
  phoneDisplay: '+92 300 047 8311',
  email: 'info@mtufaildiagnosticcenter.com',
  whatsapp: '923000478311',
  whatsappLink: 'https://wa.me/923000478311?text=Hello,%20I%20would%20like%20to%20book%20an%20appointment%20at%20Mian%20Tufail%20Diagnostic%20Center.',
  facebook: 'https://web.facebook.com/miantufaildiagnosticcenter/',
  instagram: '#',
  reviews: 'https://reviews.mtufaildiagnosticcenter.com',
  address: {
    short: 'Ferozepur Road, Kasur',
    full: 'Near New Kasur City Housing Society, Opposite New Lari Adda, Main Ferozepur Road, Kasur, Punjab, Pakistan',
    city: 'Kasur',
    province: 'Punjab',
    country: 'Pakistan',
  },
  googleMapsEmbed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2663.2211372123597!2d74.45454149999999!3d31.1482307!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3919bb001d08bffb%3A0x5f56e48422e2f8e8!2sMian%20Tufail%20Diagnostic%20Centre!5e1!3m2!1sen!2s!4v1768321577587!5m2!1sen!2s',
  googleMapsLink: 'https://www.google.com/maps/place/Mian+Tufail+Diagnostic+Centre',
};

/**
 * Navigation links
 */
export const NAV_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'Services', path: '/services' },
  { label: 'Blog', path: '/blog' },
  { label: 'FAQ', path: '/faq' },
  { label: 'Contact', path: '/contact' },
  { label: 'Book Appointment', path: '/book-appointment' },
];

/**
 * Services offered by the diagnostic center
 * Source: https://mtufaildiagnosticcenter.com
 */
export const SERVICES = [
  {
    id: 'siemens-magnetom-c2',
    title: 'Siemens Magnetom C2',
    shortTitle: 'Open MRI',
    description: 'Latest Open MRI technology for accurate diagnostics. Our Siemens Magnetom C2 provides high-quality imaging with patient comfort in mind, perfect for claustrophobic patients.',
    shortDescription: 'Latest Open MRI technology for accurate diagnostics.',
    icon: Monitor,
    image: '/images/services/siemens-mri.jpg',
    featured: true,
    details: [
      'Open design for patient comfort',
      'High-resolution imaging',
      'Suitable for claustrophobic patients',
      'Advanced Siemens technology',
      'Fast scan times',
    ],
  },
  {
    id: 'whole-body-mri',
    title: 'Whole Body MRI',
    shortTitle: 'Full Body Scan',
    description: 'Comprehensive imaging to detect abnormalities throughout the entire body. Our whole body MRI provides a complete health screening solution.',
    shortDescription: 'Comprehensive imaging to detect abnormalities.',
    icon: Scan,
    image: '/images/services/whole-body-mri.jpg',
    featured: true,
    details: [
      'Complete body assessment',
      'Early cancer detection',
      'Non-invasive screening',
      'Detailed organ visualization',
      'Comprehensive health check',
    ],
  },
  {
    id: 'mr-angiogram',
    title: 'MR Angiogram',
    shortTitle: 'Vascular Imaging',
    description: 'Advanced vascular imaging for detecting blockages and abnormalities in blood vessels. Essential for cardiovascular health assessment.',
    shortDescription: 'Advanced vascular imaging for detecting blockages.',
    icon: HeartPulse,
    image: '/images/services/mr-angiogram.jpg',
    featured: true,
    details: [
      'Blood vessel visualization',
      'Blockage detection',
      'Aneurysm screening',
      'Non-contrast options available',
      'Heart and brain vessel imaging',
    ],
  },
  {
    id: 'mr-venogram',
    title: 'MR Venogram',
    shortTitle: 'Vein Imaging',
    description: 'Detailed imaging for vein-related conditions including deep vein thrombosis (DVT) and other venous disorders.',
    shortDescription: 'Detailed imaging for vein-related conditions.',
    icon: Droplets,
    image: '/images/services/mr-venogram.jpg',
    featured: true,
    details: [
      'DVT detection',
      'Venous malformation assessment',
      'Clot visualization',
      'Lower extremity imaging',
      'Cerebral vein assessment',
    ],
  },
  {
    id: 'brain-mri',
    title: 'Brain MRI',
    shortTitle: 'Neurological Imaging',
    description: 'High-resolution brain imaging for neurological conditions, tumors, stroke assessment, and other brain-related concerns.',
    shortDescription: 'High-resolution brain imaging for neurological conditions.',
    icon: Brain,
    image: '/images/services/siemens-mri.jpg',
    featured: false,
    details: [
      'Tumor detection',
      'Stroke assessment',
      'MS diagnosis support',
      'Detailed brain structure imaging',
      'Pituitary gland evaluation',
    ],
  },
  {
    id: 'spine-mri',
    title: 'Spine MRI',
    shortTitle: 'Spinal Imaging',
    description: 'Comprehensive spine imaging for disc problems, spinal cord issues, and back pain diagnosis.',
    shortDescription: 'Comprehensive spine imaging for disc problems.',
    icon: Bone,
    image: '/images/services/siemens-mri.jpg',
    featured: false,
    details: [
      'Herniated disc detection',
      'Spinal stenosis assessment',
      'Nerve compression evaluation',
      'Cervical to lumbar imaging',
      'Post-surgical evaluation',
    ],
  },
  {
    id: 'joint-mri',
    title: 'Joint MRI',
    shortTitle: 'Musculoskeletal',
    description: 'Detailed imaging of joints including knee, shoulder, hip, and ankle for sports injuries and arthritis.',
    shortDescription: 'Detailed imaging of joints for injuries and arthritis.',
    icon: Activity,
    image: '/images/services/siemens-mri.jpg',
    featured: false,
    details: [
      'Ligament tear detection',
      'Cartilage assessment',
      'Sports injury evaluation',
      'Arthritis staging',
      'Pre-surgical planning',
    ],
  },
  {
    id: 'abdominal-mri',
    title: 'Abdominal MRI',
    shortTitle: 'Abdominal Imaging',
    description: 'Non-invasive imaging of abdominal organs including liver, kidneys, pancreas, and surrounding structures.',
    shortDescription: 'Non-invasive imaging of abdominal organs.',
    icon: Stethoscope,
    image: '/images/services/siemens-mri.jpg',
    featured: false,
    details: [
      'Liver lesion characterization',
      'Kidney evaluation',
      'Pancreatic imaging',
      'Adrenal gland assessment',
      'Abdominal mass evaluation',
    ],
  },
];

/**
 * FAQ items for the FAQ page
 */
export const FAQ_ITEMS = [
  {
    title: 'What is Mian Tufail Diagnostic Center?',
    content: 'Mian Tufail Diagnostic Center is a state-of-the-art medical imaging facility providing advanced MRI and diagnostic services using the latest Siemens technology. We are located in Kasur and serve patients from across the region with accurate and reliable diagnostic services.',
  },
  {
    title: 'What types of MRI services do you offer?',
    content: 'We offer a comprehensive range of MRI services including Siemens Magnetom C2 Open MRI, Whole Body MRI, MR Angiogram, MR Venogram, Brain MRI, Spine MRI, Joint MRI, and Abdominal MRI. Our advanced equipment ensures high-quality imaging for accurate diagnosis.',
  },
  {
    title: 'Is MRI safe?',
    content: 'Yes, MRI is a very safe imaging technique. Unlike X-rays or CT scans, MRI does not use ionizing radiation. It uses magnetic fields and radio waves to create detailed images of the body. However, patients with certain metal implants or pacemakers should inform us before the scan.',
  },
  {
    title: 'What are your operating hours?',
    content: 'We are open Monday through Saturday from 8:00 AM to 10:00 PM, and on Sundays from 9:00 AM to 6:00 PM. We recommend booking an appointment in advance to ensure availability.',
  },
  {
    title: 'How can I book an appointment?',
    content: 'You can book an appointment through our website by clicking the "Book Appointment" button, calling us at +92 300 047 8311, or reaching out via WhatsApp. Our staff will help you schedule a convenient time for your scan.',
  },
  {
    title: 'How long does an MRI scan take?',
    content: 'The duration of an MRI scan varies depending on the type of examination. Most scans take between 30 to 60 minutes. Some specialized scans like Whole Body MRI may take longer. Our staff will inform you about the expected duration when you book your appointment.',
  },
  {
    title: 'Do I need to prepare for an MRI scan?',
    content: 'Preparation depends on the type of scan. Generally, you should wear comfortable clothing without metal fasteners, remove all jewelry and metal objects, and inform us about any implants or medical conditions. For some scans, you may need to fast for a few hours before the examination.',
  },
  {
    title: 'How soon will I receive my report?',
    content: 'Routine reports are delivered within 6-8 hours after review by our experienced radiologists. Emergency report delivery is available within 2 hours for urgent or emergency cases. You will be notified when your report is ready, and you can collect it from our center or have it sent to your referring physician.',
  },
  {
    title: 'Is the Open MRI suitable for claustrophobic patients?',
    content: 'Yes! Our Siemens Magnetom C2 is an Open MRI system specifically designed for patient comfort. The open design provides a less confined space compared to traditional closed MRI machines, making it an excellent option for claustrophobic patients or those who feel anxious in enclosed spaces.',
  },
  {
    title: 'Do you accept insurance?',
    content: 'Yes, we work with various insurance providers. Please contact us with your insurance details, and our staff will help verify your coverage and assist with any necessary documentation for reimbursement.',
  },
];

/**
 * Testimonials from patients
 */
export const TESTIMONIALS = [
  {
    id: 1,
    name: 'Ahmed Khan',
    role: 'Patient',
    content: 'Excellent service and professional staff. The Open MRI was perfect for me as I am claustrophobic. The results were delivered quickly and the doctors were very helpful in explaining everything.',
    rating: 5,
  },
  {
    id: 2,
    name: 'Fatima Malik',
    role: 'Patient',
    content: 'Very impressed with the modern equipment and cleanliness of the facility. The staff made me feel comfortable throughout the whole body MRI scan. Highly recommended!',
    rating: 5,
  },
  {
    id: 3,
    name: 'Dr. Usman Ali',
    role: 'Referring Physician',
    content: 'As a physician, I trust Mian Tufail Diagnostic Center for my patients\' MRI needs. The image quality is excellent and the reports are detailed and accurate. Great collaboration!',
    rating: 5,
  },
  {
    id: 4,
    name: 'Aisha Begum',
    role: 'Patient',
    content: 'The appointment booking process was very easy through WhatsApp. The center is well-located and the parking was convenient. The staff was courteous and the scan was done on time.',
    rating: 5,
  },
  {
    id: 5,
    name: 'Rashid Mahmood',
    role: 'Patient',
    content: 'Had my spine MRI done here. The experience was smooth from start to finish. The radiologist took time to explain my results. Very professional service at reasonable rates.',
    rating: 5,
  },
];

/**
 * Stats/Trust signals for the homepage
 */
export const STATS = [
  { value: '5000+', label: 'Patients Served' },
  { value: '8+', label: 'MRI Services' },
  { value: '6-8hrs', label: 'Report Delivery' },
  { value: '99%', label: 'Patient Satisfaction' },
];

/**
 * Service type options for the booking form
 */
export const SERVICE_OPTIONS = SERVICES.map((service) => ({
  value: service.id,
  label: service.title,
}));

/**
 * Gender options for the booking form
 */
export const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Prefer Not To Say' },
];

/**
 * Time slot options for the booking form
 */
export const TIME_SLOTS = [
  { value: '08:00', label: '8:00 AM' },
  { value: '09:00', label: '9:00 AM' },
  { value: '10:00', label: '10:00 AM' },
  { value: '11:00', label: '11:00 AM' },
  { value: '12:00', label: '12:00 PM' },
  { value: '13:00', label: '1:00 PM' },
  { value: '14:00', label: '2:00 PM' },
  { value: '15:00', label: '3:00 PM' },
  { value: '16:00', label: '4:00 PM' },
  { value: '17:00', label: '5:00 PM' },
  { value: '18:00', label: '6:00 PM' },
  { value: '19:00', label: '7:00 PM' },
  { value: '20:00', label: '8:00 PM' },
];
